import React, { useEffect, useRef, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import countries from "@/constants/array/country_city.json";
import VirtualizedCitySelect from "@/components/ui/virtualized-city-select";
import { APIClient } from "@/api";
import { toast } from "sonner";
import { Loader2, Upload } from "lucide-react";

export type OfferProgramEditSource = {
  id?: number;
  programCode?: string;
  programName?: string;
  programLogoUri?: string;
  websiteUrl?: string;
  programWebsiteUrl?: string;
  address?: string;
  country?: string;
  city?: string;
};

const schema = Yup.object().shape({
  name: Yup.string()
    .required("Program name is required")
    .matches(
      /^[a-zA-Z0-9\s\-':.]+$/,
      "Program name can only contain letters, numbers, spaces, and - ' : ."
    ),
  code: Yup.string()
    .required("Program code is required")
    .matches(
      /^[a-zA-Z0-9]+$/,
      "Program code can only contain letters and numbers"
    ),
  address: Yup.string().required("Program location is required"),
  websiteUrl: Yup.string()
    .transform((value) => (value ? value : ""))
    .test("is-valid-url", "Website URL must be a valid URL format", (v) =>
      !v ? true : Yup.string().url().isValidSync(v)
    ),
  country: Yup.string().required("Country is required"),
  city: Yup.string().required("City is required"),
});

type FormValues = Yup.InferType<typeof schema>;

function findCountryJsonByName(name: string | undefined) {
  if (!name) return "";
  const c = (countries as { name: string; cities?: string[] }[]).find(
    (x) => x.name === name
  );
  return c ? JSON.stringify(c) : "";
}

function buildDefaults(program: OfferProgramEditSource): FormValues {
  return {
    name: program.programName || "",
    code: program.programCode || "",
    address: program.address || "",
    websiteUrl:
      program.websiteUrl || program.programWebsiteUrl || "",
    country: findCountryJsonByName(program.country) || "",
    city: program.city || "",
  };
}

function resolveProgramLogoUri(uri?: string) {
  if (!uri) return undefined;
  if (uri.startsWith("http")) return uri;
  const base = (import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL || "").trim();
  if (!base) return uri;
  const path = uri.startsWith("/") ? uri : `/${uri}`;
  return `${base.replace(/\/$/, "")}${path}`;
}

type EditOfferProgramSheetProps = {
  program: OfferProgramEditSource;
  programId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdated: (patch: Partial<OfferProgramEditSource>) => void;
};

export const EditOfferProgramSheet: React.FC<EditOfferProgramSheetProps> = ({
  program,
  programId,
  open,
  onOpenChange,
  onUpdated,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [logoFile, setLogoFile] = useState<File | undefined>();
  const [selectedCountry, setSelectedCountry] = useState<{
    name: string;
    cities?: string[];
  } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | undefined>(() =>
    resolveProgramLogoUri(program.programLogoUri)
  );

  const form = useForm<FormValues>({
    resolver: yupResolver(schema),
    mode: "onSubmit",
    defaultValues: buildDefaults(program),
  });

  useEffect(() => {
    if (logoFile) {
      const u = URL.createObjectURL(logoFile);
      setLogoPreviewUrl(u);
      return () => URL.revokeObjectURL(u);
    }
    setLogoPreviewUrl(resolveProgramLogoUri(program.programLogoUri));
    return undefined;
  }, [logoFile, program.programLogoUri]);

  useEffect(() => {
    if (!open) {
      setLogoFile(undefined);
      return;
    }
    form.reset(buildDefaults(program));
    try {
      const countryVal = findCountryJsonByName(program.country);
      if (countryVal) {
        setSelectedCountry(JSON.parse(countryVal));
      } else {
        setSelectedCountry(null);
      }
    } catch {
      setSelectedCountry(null);
    }
  }, [open, program, form]);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) setLogoFile(f);
  };

  const onSubmit = async (values: FormValues) => {
    setSubmitting(true);
    try {
      const selectedCountryName =
        selectedCountry?.name ||
        (() => {
          try {
            const parsed = values.country ? JSON.parse(values.country) : null;
            return parsed?.name || "";
          } catch {
            return "";
          }
        })();

      const programOfferData = {
        id: Number(programId),
        programCode: values.code,
        programName: values.name,
        websiteUrl: values.websiteUrl || "",
        address: values.address,
        country: selectedCountryName,
        city: values.city,
      };

      const fd = new FormData();
      if (logoFile) {
        fd.append("programOfferLogo", logoFile);
      }
      fd.append(
        "programOfferVO",
        new Blob([JSON.stringify(programOfferData)], {
          type: "application/json",
        })
      );

      const { data } = await APIClient(
        "/program/createOrUpdateProgramForOffer",
        fd,
        { requestType: "post", contentType: "multipart/form-data" }
      );

      const newLogoUri =
        (data as any)?.programLogoUri ?? (data as any)?.programLogoURI;

      toast.success("Program updated successfully");
      onOpenChange(false);
      setLogoFile(undefined);

      onUpdated({
        programName: values.name,
        programCode: values.code,
        websiteUrl: values.websiteUrl || "",
        address: values.address,
        country: selectedCountryName,
        city: values.city,
        ...(newLogoUri ? { programLogoUri: newLogoUri } : {}),
      });
    } catch (e: any) {
      toast.error(e?.message || "Failed to update program");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-lg md:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Edit program</SheetTitle>
          <SheetDescription>
            Update program details. Logo is optional; leave unchanged to keep
            the current image.
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-6 py-6"
          >
            <Card className="border border-gray-100 shadow-sm">
              <CardHeader className="card-header-compact bg-gray-50 py-3">
                <CardTitle className="text-base font-medium">
                  General information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                <div className="space-y-2">
                  <FormLabel>Program logo</FormLabel>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleLogoChange}
                  />
                  <div
                    className="w-24 h-24 bg-gray-100 rounded-lg flex flex-col items-center justify-center border-2 border-dashed border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {logoPreviewUrl ? (
                      <img
                        src={logoPreviewUrl}
                        alt=""
                        className="max-h-20 max-w-20 object-contain rounded"
                        onError={(ev) => {
                          ev.currentTarget.onerror = null;
                          ev.currentTarget.src = "/placeholder.svg";
                        }}
                      />
                    ) : (
                      <>
                        <Upload className="h-6 w-6 text-gray-400" />
                        <span className="text-xs text-gray-500 mt-1">Upload</span>
                      </>
                    )}
                  </div>
                </div>

                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Program name*</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          onChange={(e) => {
                            field.onChange(
                              e.target.value.replace(/[^a-zA-Z0-9\s\-':.]/g, "")
                            );
                          }}
                        />
                      </FormControl>
                      <FormDescription>
                        The name of your loyalty program
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Program code*</FormLabel>
                      <FormControl>
                        <Input {...field} disabled />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="websiteUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Website URL</FormLabel>
                      <FormControl>
                        <Input placeholder="https://example.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company address*</FormLabel>
                      <FormControl>
                        <Textarea rows={3} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="country"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Country*</FormLabel>
                        <Select
                          onValueChange={(value) => {
                            const selected = JSON.parse(value);
                            setSelectedCountry(selected);
                            field.onChange(value);
                            form.setValue("city", "");
                          }}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select country" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {(countries as any)?.map(
                              (country: { name: string }) => (
                                <SelectItem
                                  key={country.name}
                                  value={JSON.stringify(country)}
                                >
                                  {country.name}
                                </SelectItem>
                              )
                            )}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="city"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>City*</FormLabel>
                        <FormControl>
                          <VirtualizedCitySelect
                            cities={selectedCountry?.cities || []}
                            value={field.value}
                            onValueChange={field.onChange}
                            placeholder="Select city"
                            disabled={!selectedCountry?.cities?.length}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            <SheetFooter className="gap-2 sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-rose-600 hover:bg-rose-700"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving…
                  </>
                ) : (
                  "Save changes"
                )}
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
};
